'use client';

import React, { type Dispatch, type SetStateAction } from 'react';

import { useTranslations } from 'next-intl';

import { GoogleButton } from '../GoogleButton';

import { decodeJWT } from '@/modules/auth/decodeJWT';
import { SSOButtonVariant } from '@/modules/auth/types';
import {
  handleSSOError,
  setAccountLinkingCookies,
} from '@/modules/login/helpers';
import { LoginType } from '@/modules/login/types';
import { useLoginMutations } from '@/modules/login/useLoginMutations';
import { useLoginTracking } from '@/modules/login/useLoginTracking';
import { useSSOSignup } from '@/modules/signup/hooks/useSSOSignup';
import { useSignupTracking } from '@/modules/signup/hooks/useSignupTracking';
import { SignupType } from '@/modules/signup/types';

type Props = {
  nonce: string;
  setError: Dispatch<SetStateAction<string>>;
  setIsAccountLinkingError: Dispatch<SetStateAction<boolean>>;
};

export function GoogleLoginButton({
  nonce,
  setError,
  setIsAccountLinkingError,
}: Props) {
  const { sendLoginSubmitAction } = useLoginTracking();
  const { sendSignupSubmitActionEvent } = useSignupTracking();
  const { loginMutation } = useLoginMutations({ loginType: LoginType.Google });
  const t = useTranslations('login');
  const { signupWithSSO } = useSSOSignup({
    ssoType: SignupType.Google,
  });

  async function handleResponse({
    credential,
  }: google.accounts.id.CredentialResponse) {
    const { email, given_name, family_name } = decodeJWT(credential);

    function handleUserNotFound() {
      sendSignupSubmitActionEvent(SignupType.Google);

      return signupWithSSO({
        email,
        credentials: { googleIdToken: credential },
        name: { firstName: given_name, lastName: family_name },
      });
    }

    function handleAccountNotLinked() {
      setAccountLinkingCookies({ googleIdToken: credential }, email);

      setIsAccountLinkingError(true);

      return setError(
        t('SSO.AccountExistsSpecific', {
          socialProvider: 'Google',
        })
      );
    }

    loginMutation.mutate(
      { google_id_token: credential },
      {
        onError: (error) => {
          handleSSOError({
            error,
            setError,
            onUserNotFound: handleUserNotFound,
            onAccountNotLinked: handleAccountNotLinked,
          });
        },
      }
    );
  }

  return (
    <GoogleButton
      handleResponse={handleResponse}
      variant={SSOButtonVariant.EyeCatching}
      nonce={nonce}
      onSubmitTracking={() => sendLoginSubmitAction(LoginType.Google)}
    />
  );
}
